import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Button,
  ErrorState,
  PageContainer,
  Pagination,
  SurfaceCard,
} from '@react-apps/design-system';
import { useInstitutionsById } from '../hooks/use-institutions';
import {
  useQuestionFilterCascade,
  useQuestionSearch,
} from '../hooks/use-question-search';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  deepestSubjectId,
  dropInvalidQuestionFilters,
  EMPTY_QUESTION_FILTERS,
  hasQuestionFilter,
  sameQuestionFilters,
  type QuestionSearchFilters,
} from '../question-search-filters';
import { QuestionSearchFilters as Filters } from '../components/QuestionSearchFilters';
import {
  QuestionSearchCard,
  QuestionSearchCardSkeleton,
} from '../components/QuestionSearchCard';
import { Switch } from '../components/Switch';

const PER_PAGE = 20;

/**
 * Buscar e Limpar: largura total e alvo de 44 px empilhados; inativos por
 * `aria-disabled`, com o mesmo visual do `disabled` do design system.
 */
const ACTION_CLASS =
  'min-h-11 w-full lg:min-h-0 lg:w-auto aria-disabled:cursor-not-allowed aria-disabled:opacity-50';
/** Sem o hover de botão clicável quando inativo (o `disabled` o tirava com `pointer-events-none`). */
const PRIMARY_INACTIVE = 'aria-disabled:hover:bg-blue-600';
const SECONDARY_INACTIVE = 'aria-disabled:hover:bg-gray-200';
const SKELETON_CARDS = 5;

/** O único "o que buscar" desta tela; o resto é da Gestão de comentários. */
const REPORTED_QUESTION = 'REPORTED_QUESTION';

type PageFilters = QuestionSearchFilters & { demand: string };

const EMPTY_PAGE_FILTERS: PageFilters = {
  ...EMPTY_QUESTION_FILTERS,
  demand: '',
};

/**
 * Buscar questões (DESEN-42): os filtros de assunto, prova e ano, o "Só
 * questões com report" e a lista de cards. Só questão publicada.
 *
 * - **Filtros e página vivem na URL**, como na busca de provas: abrir uma
 *   questão e voltar devolve a mesma busca. Mudar um filtro volta à página 1.
 * - **Parâmetro inválido na URL sai em silêncio**: UF fora da lista, ano que
 *   não é inteiro e `demand` que não é o desta tela já na leitura; assunto,
 *   instituição ou ano que não existe, quando as opções chegam.
 * - **Os selects editam um rascunho; só o Buscar busca.** A URL é a busca
 *   aplicada: abrir a tela (ou voltar da ficha) mostra a lista dela sem
 *   clique, e a paginação usa o aplicado. As opções da cascata seguem o
 *   rascunho, porque são elas que liberam o nível de baixo. Limpar zera os
 *   dois de uma vez. A contagem é anunciada (`aria-live`).
 */
export function QuestionSearchPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const institutions = useInstitutionsById();

  const fromUrl = useMemo(
    () => readFiltersParams(searchParams, EMPTY_PAGE_FILTERS),
    [searchParams],
  );
  const page = readPageParam(searchParams);

  // As opções de cada nível dependem do pai já conferido na forma; o que
  // existe se confere contra elas.
  const preliminary = dropInvalidQuestionFilters(fromUrl, {
    institutionsById: institutions.data,
  });
  const { areas, specialties, themes } = useQuestionFilterCascade(preliminary);
  const filters: PageFilters = {
    ...dropInvalidQuestionFilters(preliminary, {
      institutionsById: institutions.data,
      areaIds: areas.data?.subjects.map((s) => s.id),
      specialtyIds: specialties.data?.subjects.map((s) => s.id),
      themeIds: themes.data?.subjects.map((s) => s.id),
      years: areas.data?.years,
    }),
    demand: fromUrl.demand === REPORTED_QUESTION ? REPORTED_QUESTION : '',
  };
  const same =
    sameQuestionFilters(filters, fromUrl) && filters.demand === fromUrl.demand;

  const update = (next: PageFilters, nextPage = 1) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_PAGE_FILTERS);
        writePageParam(params, nextPage);
        return params;
      },
      { replace: true },
    );
  };

  // O que a URL trazia de inválido sai dela também, para o "Voltar" da
  // questão não devolver um filtro que a tela já descartou. A página volta à
  // 1: sem o filtro, a lista é outra, e a página 4 dela é uma qualquer.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (!same) update(filters, 1);
    // `update` e `filters` mudam a cada render; o gatilho é a diferença.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [same, filtersKey]);

  // O rascunho dos selects. Volta a ser o aplicado quando a chave dos filtros
  // aplicados muda (Voltar, Limpar, o descarte do inválido acima), e não com a
  // página: paginar e o `pastTheEnd` não apagam o que a pessoa escolheu e
  // ainda não buscou. Durante o render, como no `StoredFilesPage`, e sem `key`
  // no form, para o foco ficar no botão que aplicou.
  const [draft, setDraft] = useState<PageFilters>(filters);
  const [syncedKey, setSyncedKey] = useState(filtersKey);
  if (syncedKey !== filtersKey) {
    setSyncedKey(filtersKey);
    setDraft(filters);
  }
  const dirty =
    !sameQuestionFilters(draft, filters) || draft.demand !== filters.demand;
  const draftFiltered = hasQuestionFilter(draft) || draft.demand !== '';
  const searchHintId = useId();
  const clearHintId = useId();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (dirty) update(draft, 1);
  };
  const clear = () => {
    setDraft(EMPTY_PAGE_FILTERS);
    update(EMPTY_PAGE_FILTERS);
  };

  // A busca espera o que ainda está sendo conferido. Consulta que falhou
  // conta como conferida: o filtro fica, e a lista segue com ele.
  const settled =
    (!preliminary.institutionId || institutions.isFetched) &&
    ((!preliminary.area && !preliminary.year) || areas.isFetched) &&
    (!preliminary.specialty || specialties.isFetched) &&
    (!preliminary.theme || themes.isFetched);

  const search = useQuestionSearch(
    {
      page,
      perPage: PER_PAGE,
      subjectId: deepestSubjectId(filters) || undefined,
      uf: filters.uf || undefined,
      institutionId: filters.institutionId || undefined,
      year: filters.year ? Number(filters.year) : undefined,
      demand: filters.demand ? REPORTED_QUESTION : undefined,
    },
    settled && same,
  );

  const filtered = hasQuestionFilter(filters) || filters.demand !== '';
  const canClear = filtered || draftFiltered;
  const total = search.data?.total ?? 0;
  const items = search.data?.items ?? [];
  const from = `${location.pathname}${location.search}`;
  const refreshing = search.isFetching && search.isPlaceholderData;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  // Página além do fim (o conjunto encolheu desde que a URL foi escrita, ou
  // a URL é velha): vai para a última que existe, em vez de dizer que não há
  // questão nenhuma e sumir com a paginação.
  const pastTheEnd =
    search.isSuccess && !refreshing && items.length === 0 && page > lastPage;
  useEffect(() => {
    if (pastTheEnd) update(filters, lastPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastTheEnd, lastPage]);

  // Primeira carga, ou a lista anterior está vazia enquanto a nova carrega
  // (inclusive a ida à última página): o esqueleto, e não o vazio, que seria
  // falso por um instante. O total da busca anterior também não aparece.
  const skeleton =
    !search.data || pastTheEnd || (refreshing && items.length === 0);
  const countLabel = `${total} ${total === 1 ? 'questão encontrada' : 'questões encontradas'}`;

  return (
    <PageContainer maxWidth="6xl">
      <h1 className="text-(--color-text-primary) text-xl font-semibold">
        Buscar questões
      </h1>

      <SurfaceCard className="space-y-5">
        <form onSubmit={submit} className="space-y-3">
          <Filters
            value={draft}
            onChange={(next) =>
              setDraft((prev) => ({ ...next, demand: prev.demand }))
            }
          />
          {/* À esquerda o "Só questões com report" e o Buscar, lado a lado; o
              Limpar no canto direito. `aria-disabled`, e não `disabled`: o
              botão desabilitado perde o foco, e quem aplicou pelo teclado
              cairia no `body`. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
              <Switch
                label="Só questões com report"
                checked={draft.demand === REPORTED_QUESTION}
                onChange={(on) =>
                  setDraft((prev) => ({
                    ...prev,
                    demand: on ? REPORTED_QUESTION : '',
                  }))
                }
              />
              <Button
                type="submit"
                size="sm"
                aria-disabled={!dirty}
                aria-describedby={dirty ? undefined : searchHintId}
                className={`${ACTION_CLASS} ${PRIMARY_INACTIVE}`}
              >
                Buscar
              </Button>
              <span id={searchHintId} className="sr-only">
                Altere um filtro para buscar
              </span>
            </div>
            <span id={clearHintId} className="sr-only">
              Nenhum filtro para limpar
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-disabled={!canClear}
              aria-describedby={canClear ? undefined : clearHintId}
              className={`${ACTION_CLASS} ${SECONDARY_INACTIVE}`}
              onClick={() => {
                if (canClear) clear();
              }}
            >
              Limpar filtros
            </Button>
          </div>
        </form>

        {/* A contagem fica na tela o tempo todo (sem ela a lista subiria uma
            linha a cada busca), e o anúncio vai à parte, só quando a busca
            termina: com a lista anterior ainda na tela, o leitor de tela
            anunciaria o total velho e, em seguida, o novo. */}
        <p className="text-(--color-text-secondary) min-h-5 text-sm">
          {search.isSuccess && !skeleton ? countLabel : ''}
        </p>
        <span className="sr-only" aria-live="polite">
          {search.isSuccess && !search.isFetching ? countLabel : ''}
        </span>

        {search.isError ? (
          <ErrorState
            message="Não foi possível carregar as questões."
            onRetry={() => void search.refetch()}
          />
        ) : skeleton ? (
          <div className="space-y-3">
            {Array.from({ length: SKELETON_CARDS }, (_, i) => (
              <QuestionSearchCardSkeleton key={i} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-(--color-text-secondary) py-10 text-center text-sm">
            {filtered
              ? 'Nenhuma questão encontrada com esses filtros. Remova um filtro ou use Limpar filtros.'
              : 'Ainda não há questões publicadas.'}
          </p>
        ) : (
          <>
            <ul
              aria-busy={refreshing}
              className={`space-y-3 transition-opacity ${refreshing ? 'opacity-60' : ''}`}
            >
              {items.map((card) => (
                <li key={card.questionId}>
                  <QuestionSearchCard
                    card={card}
                    reportCount={card.openQuestionReports}
                    reportLabel="na questão"
                    from={from}
                  />
                </li>
              ))}
            </ul>
            <Pagination
              page={page}
              total={total}
              perPage={PER_PAGE}
              loading={search.isFetching}
              label="questão"
              pluralLabel="questões"
              onPageChange={(next) => update(filters, next)}
            />
          </>
        )}
      </SurfaceCard>
    </PageContainer>
  );
}
